import { Autocomplete, Box, MenuItem, TextField } from '@mui/material';
import useEntertainmentOptions from './useEntertainmentOptions';

// Criteria offered by both the "Bedingung" block (one path) and the
// "Bedingter Split" (two paths). Grouped so the long list stays scannable.
export const CONDITION_TYPE_GROUPS = [
  {
    label: 'Eigenschaften des Kontakts',
    types: {
      zipPrefix: 'PLZ beginnt mit… (Geo-Match)',
      hasTag: 'Hat Tag',
      notHasTag: 'Hat Tag nicht',
      ageBetween: 'Alter zwischen … und … Jahren',
    },
  },
  {
    label: 'Bisheriges Verhalten',
    types: {
      visitedEntertainment: 'Hat Entertainment bereits besucht',
      notVisitedEntertainment: 'Hat Entertainment noch nicht besucht (Cross-Selling)',
      bookingCountAtLeast: 'Mindestanzahl Buchungen',
      revenueAtLeast: 'Mindestumsatz (€)',
      lastVisitWithinDays: 'Besuch innerhalb der letzten X Tage',
      lastVisitOlderThanDays: 'Kein Besuch seit X Tagen',
    },
  },
  {
    label: 'Reaktion im Flow',
    types: {
      redeemedVoucherFromBlock: 'Gutschein aus Block eingelöst',
      notRedeemedVoucherFromBlock: 'Gutschein aus Block NICHT eingelöst',
      redeemedCampaignCode: 'Gutschein mit Kampagnencode eingelöst',
    },
  },
];

export const CONDITION_TYPE_LABELS = CONDITION_TYPE_GROUPS.reduce(
  (all, group) => ({ ...all, ...group.types }),
  {}
);

// The criteria selector plus the fields the selected criterion needs.
const ConditionCriteriaFields = ({ block, blocks = [], readOnly, onConfigChange }) => {
  const entertainments = useEntertainmentOptions();
  const config = block.config || {};
  const conditionType = config.conditionType || 'zipPrefix';

  const selectedEntertainment =
    entertainments.find(
      entertainment => String(entertainment._id) === String(config.entertainmentId)
    ) || null;
  const voucherBlocks = blocks.filter(candidate => candidate.type === 'issueVoucher');

  return (
    <>
      <TextField
        select
        size='small'
        label='Bedingung'
        value={conditionType}
        disabled={readOnly}
        onChange={event => onConfigChange({ conditionType: event.target.value })}
      >
        {CONDITION_TYPE_GROUPS.flatMap(group => [
          <MenuItem key={group.label} disabled sx={{ opacity: 1, fontWeight: 600, fontSize: 12 }}>
            {group.label}
          </MenuItem>,
          ...Object.entries(group.types).map(([value, label]) => (
            <MenuItem key={value} value={value} sx={{ pl: 3 }}>
              {label}
            </MenuItem>
          )),
        ])}
      </TextField>

      {conditionType === 'zipPrefix' && (
        <TextField
          key={`${block.blockId}-zip`}
          size='small'
          label='PLZ-Präfixe (kommagetrennt, z. B. 60, 61, 63)'
          defaultValue={(config.zipPrefixes || []).join(', ')}
          disabled={readOnly}
          onChange={event =>
            onConfigChange({
              zipPrefixes: event.target.value
                .split(',')
                .map(prefix => prefix.trim())
                .filter(Boolean),
            })
          }
          helperText='Geprüft wird die PLZ der letzten Buchung des Kontakts.'
        />
      )}

      {['visitedEntertainment', 'notVisitedEntertainment'].includes(conditionType) && (
        <Autocomplete
          size='small'
          options={entertainments}
          value={selectedEntertainment}
          disabled={readOnly}
          onChange={(event, value) => onConfigChange({ entertainmentId: value?._id || null })}
          getOptionLabel={option => option?.longName || option?.entertainmentName || ''}
          isOptionEqualToValue={(a, b) => String(a?._id) === String(b?._id)}
          renderInput={params => <TextField {...params} label='Entertainment' />}
        />
      )}

      {['hasTag', 'notHasTag'].includes(conditionType) && (
        <TextField
          key={`${block.blockId}-tags`}
          size='small'
          label='Tags (kommagetrennt, eine Übereinstimmung genügt)'
          defaultValue={(config.tags || []).join(', ')}
          disabled={readOnly}
          onChange={event =>
            onConfigChange({
              tags: event.target.value
                .split(',')
                .map(tag => tag.trim())
                .filter(Boolean),
            })
          }
        />
      )}

      {conditionType === 'bookingCountAtLeast' && (
        <TextField
          key={`${block.blockId}-minBookings`}
          size='small'
          type='number'
          label='Mindestanzahl bestätigter Buchungen'
          defaultValue={Number(config.minBookings) || 2}
          disabled={readOnly}
          inputProps={{ min: 1 }}
          onChange={event =>
            onConfigChange({ minBookings: Math.max(1, Number(event.target.value) || 1) })
          }
        />
      )}

      {conditionType === 'revenueAtLeast' && (
        <TextField
          key={`${block.blockId}-minRevenue`}
          size='small'
          type='number'
          label='Mindestumsatz über alle Buchungen (€)'
          defaultValue={Number(config.minRevenue) || 100}
          disabled={readOnly}
          inputProps={{ min: 1 }}
          onChange={event =>
            onConfigChange({ minRevenue: Math.max(1, Number(event.target.value) || 1) })
          }
        />
      )}

      {['lastVisitWithinDays', 'lastVisitOlderThanDays'].includes(conditionType) && (
        <TextField
          key={`${block.blockId}-days`}
          size='small'
          type='number'
          label='Tage'
          defaultValue={Number(config.days) || 90}
          disabled={readOnly}
          inputProps={{ min: 1 }}
          onChange={event => onConfigChange({ days: Math.max(1, Number(event.target.value) || 1) })}
        />
      )}

      {['redeemedVoucherFromBlock', 'notRedeemedVoucherFromBlock'].includes(conditionType) && (
        <TextField
          select
          size='small'
          label='„Gutschein erstellen“-Block'
          value={config.blockId || ''}
          disabled={readOnly}
          onChange={event => onConfigChange({ blockId: event.target.value })}
          helperText={
            voucherBlocks.length
              ? 'Prüft die Einlösung der Gutscheine, die dieser Block im aktuellen Zyklus für den Kontakt erstellt hat — z. B. für Reminder nur an Nicht-Einlöser.'
              : 'Es gibt noch keinen „Gutschein erstellen“-Block in diesem Flow.'
          }
        >
          {voucherBlocks.map(candidate => (
            <MenuItem key={candidate.blockId} value={candidate.blockId}>
              Gutschein erstellen ({Number(candidate.config?.amount) || 0} €) — {candidate.blockId}
            </MenuItem>
          ))}
        </TextField>
      )}

      {conditionType === 'redeemedCampaignCode' && (
        <TextField
          key={`${block.blockId}-campaignCode`}
          size='small'
          label='Kampagnencode'
          defaultValue={config.campaignCode || ''}
          disabled={readOnly}
          placeholder='BDAY-ALL-EUR25-AUG26'
          onChange={event => onConfigChange({ campaignCode: event.target.value.trim() })}
          helperText='Prüft, ob der Kontakt einen Gutschein mit diesem Kampagnencode eingelöst hat — auch Codes, die außerhalb dieses Flows ausgegeben wurden.'
        />
      )}

      {conditionType === 'ageBetween' && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            key={`${block.blockId}-minAge`}
            size='small'
            type='number'
            label='Mindestalter'
            defaultValue={config.minAge ?? ''}
            disabled={readOnly}
            inputProps={{ min: 0 }}
            onChange={event => {
              const raw = event.target.value;
              onConfigChange({ minAge: raw === '' ? null : Math.max(0, Number(raw)) });
            }}
          />
          <TextField
            key={`${block.blockId}-maxAge`}
            size='small'
            type='number'
            label='Höchstalter'
            defaultValue={config.maxAge ?? ''}
            disabled={readOnly}
            inputProps={{ min: 0 }}
            onChange={event => {
              const raw = event.target.value;
              onConfigChange({ maxAge: raw === '' ? null : Math.max(0, Number(raw)) });
            }}
          />
        </Box>
      )}
    </>
  );
};

export default ConditionCriteriaFields;
